import { DuplicateUnitPopup } from "@src/components/deprecated/DuplicateUnitPopup/DuplicateUnitPopup";
import { Popup } from "@src/components/deprecated/Popup/Popup";
import { getCachedApiSettings } from "@src/data-hooks/general-settings/settings.service";
import { duplicatePropertyUnit } from "@src/data-hooks/unit/unit.service";
import { getPluralTenantUnitTitle, getSingularTenantUnitTitle } from "@src/utils/data/roles.utils";
import { isFieldEnabled } from "@src/utils/settings/settings.utils";
import { getFunctions, httpsCallable } from "firebase/functions";
import { t } from "i18next";
import { DataTableData, DataTableDataCellValue, DataTableHeader } from "../../../../../components/deprecated/DataTable/DataTable";
import { FirebaseDataTableHelper } from "../../../../../components/deprecated/DataTable/FirebaseDataTable/FirebaseDataTableHelper";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { PropertyData } from "../../../Property/PropertyData";
import { UnitData } from "../../../Property/Units/UnitData";
import { Role } from "../../../User/Role";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataUserField } from "../../DataObjectField/CustomFields/FirebaseDataUserField";
import { PropertyDataFloorsField } from "../../DataObjectField/CustomFields/PropertyFields";
import {
  TenantTypeField,
  UnitDataIncomeDataField,
  UnitDataIndexRegulationField,
  UnitDataJointAreaField,
  UnitDataTypeField,
} from "../../DataObjectField/CustomFields/UnitDataFields";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../../DataObjectField/Generic/FirebaseDataNumberField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { LocalData } from "../../LocalData";

export class UnitDataHelper extends DataObjectHelper<UnitData> {
  id = () => "unit";
  displayName = () => getSingularTenantUnitTitle(undefined);
  displayNamePlural = () => getPluralTenantUnitTitle(undefined);

  property = new FirebaseDataPropertyField<UnitData>("property", t("main.property"), (data) => data.property, "label");
  isArchived = new FirebaseDataBooleanField<UnitData>(
    "isArchived",
    t("main.isArchived"),
    (data) => data.archived || (data.parent as PropertyData | undefined)?.archived,
    "label"
  );
  archiveDate = new FirebaseDataDateField<UnitData>("archiveDate", t("main.archiveDate"), (data) => data.archiveDate, "label");

  name = new FirebaseDataStringField<UnitData>("name", t("main.name"), (data) => data.name, "title");
  tenantType = new TenantTypeField<UnitData>("tenantType", t("main.tenantType"), (data) => data.tenantType?.value, "label");
  type = new UnitDataTypeField<UnitData>("type", t("main.type"), (data) => data.type?.value, "label");
  orgNumber = new FirebaseDataStringField<UnitData>("orgNumber", t("main.orgNumber"), (data) => data.orgNumber?.value, "corporate_fare");
  floors = new PropertyDataFloorsField<UnitData>("floors", t("main.floors"), (data) => data.floors?.value, "trending_up", true);
  postAddress = new FirebaseDataStringField<UnitData>("postAddress", t("main.postAddress"), (data) => data.postAddress?.value, "alternate_email");
  area = new FirebaseDataNumberField<UnitData>("area", t("main.area"), (data) => data.area?.value, "crop_free", false, {
    metric: "sqm",
    decimals: 1,
  });
  parkingSpots = new FirebaseDataNumberField<UnitData>("parkingSpots", t("main.parkingSpots"), (data) => data.parkingSpots?.value, "local_parking");
  snr = new FirebaseDataStringField<UnitData>("snr", t("main.snr"), (data) => data.snr?.value, "label");
  apartmentNumber = new FirebaseDataStringField<UnitData>(
    "apartmentNumber",
    t("main.apartmentNumber"),
    (data) => data.apartmentNumber?.value,
    "label"
  );
  streetNumber = new FirebaseDataStringField<UnitData>("streetNumber", t("main.streetNumber"), (data) => data.streetNumber?.value, "label");
  fireSafetyResponsibles = new FirebaseDataUserField<UnitData>(
    "fireSafetyResponsibles",
    t("main.fireSafetyResponsibles"),
    (data) => data.fireSafetyResponsibles?.value,
    "place",
    true
  );
  contactInformation = new FirebaseDataStringField<UnitData>(
    "contactInformation",
    t("main.contactInformation"),
    (data) => data.contactInformation?.value,
    "label"
  );
  contactEmail = new FirebaseDataStringField<UnitData>("contactEmail", t("main.contactEmail"), (data) => data.contactEmail?.value, "label");
  contactPhone = new FirebaseDataStringField<UnitData>("contactPhone", t("main.contactPhone"), (data) => data.contactPhone?.value, "label");
  bedroomCount = new FirebaseDataNumberField<UnitData>("bedroomCount", t("main.bedroomCount"), (data) => data.bedroomCount?.value, "label");
  furnitured = new FirebaseDataBooleanField<UnitData>("furnitured", t("main.furnitured"), (data) => data.furnitured?.value, "label");
  incomeData = new UnitDataIncomeDataField<UnitData>("incomeData", t("main.annualRent"), (data) => data.incomeData?.value, "label");
  vatApplicable = new FirebaseDataNumberField<UnitData>(
    "vatApplicable",
    t("main.vatApplicable"),
    (data) => data.vatApplicable?.value,
    "price_check",
    false,
    { metric: "percentage" }
  );
  receivesEhfInvoice = new FirebaseDataBooleanField<UnitData>(
    "receivesEhfInvoice",
    t("main.receivesEhfInvoice"),
    (data) => data.receivesEhfInvoice?.value,
    "receipt"
  );
  contractConclusionDate = new FirebaseDataDateField<UnitData>(
    "contractConclusionDate",
    t("main.contractConclusionDate"),
    (data) => data.contractConclusionDate?.value,
    "start"
  );
  contractStartDate = new FirebaseDataDateField<UnitData>(
    "contractStartDate",
    t("main.contractStartDate"),
    (data) => data.contractStartDate?.value,
    "start"
  );
  contractEndDate = new FirebaseDataDateField<UnitData>(
    "contractEndDate",
    t("main.contractEndDate"),
    (data) => data.contractEndDate?.value,
    "last_page"
  );
  runningRenewal = new FirebaseDataNumberField<UnitData>(
    "runningRenewal",
    t("main.runningRenewal"),
    (data) => data.runningRenewal?.value,
    "label",
    false,
    { metric: "months" }
  );
  optionDuration = new FirebaseDataNumberField<UnitData>(
    "optionDuration",
    t("main.optionDuration"),
    (data) => data.optionDuration?.value,
    "label",
    false,
    { metric: "months" }
  );
  optionDeadline = new FirebaseDataDateField<UnitData>("optionDeadline", t("main.optionDeadline"), (data) => data.optionDeadline?.value, "label");
  optionNotification = new FirebaseDataDateField<UnitData>(
    "optionNotification",
    t("main.optionNotification"),
    (data) => data.optionNotification?.value,
    "label"
  );
  indexRegulation = new UnitDataIndexRegulationField<UnitData>(
    "indexRegulation",
    t("main.indexRegulation"),
    (data) => data.indexRegulation?.value,
    "label"
  );
  fixedRegulation = new FirebaseDataNumberField<UnitData>(
    "fixedRegulation",
    t("main.fixedRegulation"),
    (data) => data.fixedRegulation?.value,
    "label",
    false,
    { metric: "percentage" }
  );
  regulationInterval = new FirebaseDataNumberField<UnitData>(
    "regulationInterval",
    t("main.regulationInterval"),
    (data) => data.regulationInterval?.value,
    "label",
    false,
    { metric: "months" }
  );
  lastRegulation = new FirebaseDataDateField<UnitData>("lastRegulation", t("main.lastRegulation"), (data) => data.lastRegulation?.value, "label");
  jointArea = new UnitDataJointAreaField<UnitData>("jointArea", t("main.jointArea"), (data) => data.jointArea?.value, "label", false);
  guarantee = new FirebaseDataStringField<UnitData>("guarantee", t("main.guarantee"), (data) => data.guarantee?.value, "label");
  waterRent = new FirebaseDataNumberField<UnitData>("waterRent", t("main.waterRent"), (data) => data.waterRent?.value, "label", false, {
    metric: "currency",
    decimals: 0,
  });
  electricityRent = new FirebaseDataNumberField<UnitData>(
    "electricityRent",
    t("main.electricityRent"),
    (data) => data.electricityRent?.value,
    "label",
    false,
    { metric: "currency", decimals: 0 }
  );
  accountNumber = new FirebaseDataStringField<UnitData>("accountNumber", t("main.accountNumber"), (data) => data.accountNumber?.value, "label");
  marketRent = new FirebaseDataNumberField<UnitData>("marketRent", t("main.marketRent"), (data) => data.marketRent?.value, "label", false, {
    metric: "currency",
    decimals: 0,
  });
  note = new FirebaseDataStringField<UnitData>("note", t("main.note"), (data) => data.note?.value, "notes", false, true);

  annualRent = new FirebaseDataNumberField<UnitData>("annualRent", t("main.annualRent"), (data) => data?.totalRent(), "label", false, {
    metric: "currency",
    decimals: 0,
  });
  annualAreaRent = new FirebaseDataNumberField<UnitData>("annualAreaRent", t("main.annualAreaRent"), (data) => data.areaRent(), "label", false, {
    metric: "currency",
    decimals: 0,
  });
  annualParkingRent = new FirebaseDataNumberField<UnitData>(
    "annualParkingRent",
    t("main.annualParkingRent"),
    (data) => data.parkingRent(),
    "label",
    false,
    { metric: "currency", decimals: 0 }
  );
  annualOtherRent = new FirebaseDataNumberField<UnitData>("annualOtherRent", t("main.annualOtherRent"), (data) => data.otherRent(), "label", false, {
    metric: "currency",
    decimals: 0,
  });

  override getAllDatas(): UnitData[] {
    const units: UnitData[] = [];
    for (const p of LocalData.currentUser.properties.filter((property) => !property.deleted)) {
      units.push(...p.units);
    }
    return units;
  }

  override allFields() {
    return [this.name, this.property, ...this.getInfoFields(), this.annualRent, this.annualAreaRent, this.annualParkingRent, this.annualOtherRent];
  }

  getInfoFields(): DataObjectField<any, UnitData>[] {
    return [
      this.isArchived,
      this.tenantType,
      this.type,
      this.orgNumber,
      this.floors,
      this.postAddress,
      this.area,
      this.parkingSpots,
      this.snr,
      this.apartmentNumber,
      this.streetNumber,
      this.fireSafetyResponsibles,
      this.contactInformation,
      this.contactEmail,
      this.contactPhone,
      this.bedroomCount,
      this.furnitured,
      this.incomeData,
      this.vatApplicable,
      this.receivesEhfInvoice,
      this.contractConclusionDate,
      this.contractStartDate,
      this.contractEndDate,
      this.runningRenewal,
      this.optionDuration,
      this.optionDeadline,
      this.optionNotification,
      this.indexRegulation,
      this.fixedRegulation,
      this.regulationInterval,
      this.lastRegulation,
      this.jointArea,
      this.guarantee,
      this.waterRent,
      this.electricityRent,
      this.accountNumber,
      this.marketRent,
      this.note,
    ];
  }

  getAllDataTableDatas(documents: UnitData[]): DataTableData[] {
    const settings = getCachedApiSettings();
    return documents.map((data) => {
      function getElement(objectField: DataObjectField<any, UnitData>) {
        if (!data.infoFieldsLoaded)
          return new Promise<DataTableDataCellValue>((resolve) => {
            data.loadInfoFields(() => {
              resolve(FirebaseDataTableHelper.convertDataObjectFieldToDataTableElement(objectField, data));
            });
          });
        return FirebaseDataTableHelper.convertDataObjectFieldToDataTableElement(objectField, data);
      }

      const returnData: DataTableData = {
        key: data.parent?.id ? data.parent.id + "-" + data.id : data.id,
        cells: {
          name: data.name,
          property: data.property?.name,
        },
      };
      const financeModuleEnabled = data.property?.moduleIsActivated("tenantEconomy") ?? false;
      for (const field of this.allFields().filter((field) => isFieldEnabled(field.key, "unit", financeModuleEnabled, settings?.data))) {
        returnData.cells[field.key] = getElement(field);
      }

      return returnData;
    });
  }
  getAllDataTableHeaders(): DataTableHeader[] {
    const headers: DataTableHeader[] = [
      {
        key: "name",
        title: t("main.name"),
      },
      { key: "property", title: t("main.property") },
    ];
    const financeModuleEnabled = LocalData.currentUser.properties.some((p) => p.moduleIsActivated("tenantEconomy"));
    const settings = getCachedApiSettings();
    const fields = this.allFields().filter((field) => isFieldEnabled(field.key, "unit", financeModuleEnabled, settings?.data));
    for (const field of fields) {
      if (!headers.some((h) => h.key === field.key)) {
        headers.push({ key: field.key, title: field.title, icon: field.iconName });
      }
    }
    return headers;
  }

  getLinkTo(key: string): string | undefined {
    if (key.split("-").length >= 2) {
      const propertyID = key.split("-")[0];
      // Join all parts of the unit ID that might have been split by the "-" character
      const unitID = key.split("-").slice(1).join("-");
      return "/properties/" + propertyID + "/units/" + unitID;
    }
    return "/tenant?tId=" + key;
  }

  override getUniqueIDForData(data: UnitData): string {
    const parentID = data.parent?.id;
    return parentID ? parentID + "-" + data.id : data.id;
  }

  areAllLoaded() {
    return !LocalData.currentUser.properties.some((p) => !p.unitsLoaded || !p.units.some((u) => !u.infoFieldsLoaded));
  }

  loadAll() {
    return Promise.all(
      LocalData.currentUser.properties.map((property) => {
        return new Promise<UnitData[]>((resolveC) => {
          property.loadUnits(() => {
            const infoFieldsToLoad = property.units.filter((u) => !u.infoFieldsLoaded);
            if (infoFieldsToLoad.length === 0) {
              resolveC(property.units);
            } else {
              Promise.all(
                infoFieldsToLoad.map((u) => {
                  return new Promise<UnitData>((resolveU) => {
                    u.loadInfoFields(() => {
                      resolveU(u);
                    });
                  });
                })
              ).then((units) => {
                resolveC(units);
              });
            }
          });
        });
      })
    ).then((units) => {
      const flatList: UnitData[] = [];
      for (const list of units) {
        for (const unit of list) {
          if (!flatList.includes(unit)) flatList.push(unit);
        }
      }
      return flatList.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  override getActionsForData = (data: UnitData): DataObjectAction<UnitData>[] => {
    const member = data.property ? data.property.members.getData(LocalData.currentUser.id) : undefined;
    const admin = member ? member.isAdmin : false;
    const unitName = getPluralTenantUnitTitle(undefined);
    const actions: DataObjectAction<UnitData>[] = [];
    if (admin && member?.roles.includes(Role.LANDLORD)) {
      const archiveTitle = data.archived ? t("main.unArchive") : t("main.archive");
      actions.push({
        id: data.archived ? "unarchive" : "archive",
        iconName: "archive",
        title: archiveTitle + " " + unitName.toLowerCase(),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            data.archived
              ? PopupTemplates.presentVerifyAction(
                  dataObjects.length > 1 ? t("main.unarchiveTenantUnits") : t("main.unarchiveTenantUnit") + "?",
                  dataObjects.length > 1 ? t("main.tenantUnitsDearchivationCaution") : t("main.tenantUnitDearchivationCaution"),
                  () => {
                    resolve({ dataObjects });
                  }
                )
              : PopupTemplates.presentVerifyAction(
                  dataObjects.length > 1 ? t("main.archiveTenantUnits") : t("main.archiveTenantUnit") + "?",
                  dataObjects.length > 1 ? t("main.tenantUnitsArchivationCaution") : t("main.tenantUnitArchivationCaution"),
                  () => {
                    resolve({ dataObjects });
                  }
                );
          });
        },
        performAction: () => {
          const isArchived = data.archived;
          let updateData: { [key: string]: any } = {};
          if (isArchived) {
            updateData = {
              archived: false,
              archiveDate: null,
            };
          } else {
            updateData = {
              archived: true,
              archiveDate: new Date(),
            };
          }
          return ToastUtilities.presentSavingToast(
            data.saveUpdate(updateData).then(() => {
              data.archived = !isArchived;
            })
          );
        },
      });
      actions.push({
        id: "duplicate",
        iconName: "content_copy",
        title: t("main.duplicate") + " " + unitName.toLowerCase(),
        requiredConsent: (dataObjects) => {
          if (dataObjects.length > 1) {
            return new Promise((resolve) => {
              PopupTemplates.presentVerifyAction(
                t("propertyUnits.actions.duplicate.title", { unitName: unitName }) + "?",
                t("propertyUnits.actions.duplicate.description", { unitName: unitName }),
                () => {
                  resolve({ dataObjects });
                }
              );
            });
          } else {
            return new Promise((resolve) => {
              Popup.present((id) => (
                <DuplicateUnitPopup
                  id={id}
                  defaultName={data.name}
                  defaultPropertyID={data.property?.id ?? ""}
                  onConfirm={(id, name, propertyID) => {
                    Popup.remove(id);
                    resolve({ dataObjects, data: { name, propertyID } });
                  }}
                />
              ));
            });
          }
        },
        performAction: (newData) => {
          const { name, propertyID } = newData;
          const newName = typeof name === "string" ? name : `${data.name} (${t("main.copy")})`;
          return ToastUtilities.presentPromise(
            t("main.saving"),
            duplicatePropertyUnit({ unitID: data.id, name: newName, fromPropertyID: data.property?.id ?? "ERROR", toPropertyID: propertyID })
          );
        },
      });
      actions.push({
        id: "delete",
        iconName: "delete",
        title: t("main.delete") + " " + unitName.toLowerCase(),
        iconColor: "var(--red-400)",
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyDeleteDatas(
              this,
              dataObjects.map((data) => data.name),
              () => {
                resolve({ dataObjects });
              },
              t("main.unitDeletionCaution")
            );
          });
        },
        performAction: () => {
          return ToastUtilities.presentPromise(t("main.deleting"), data.deleteTenant());
        },
      });
    }
    return actions;
  };

  override saveUpdate(data: UnitData, updatedRawData: { [key: string]: any }): Promise<any> {
    const fields: { [key: string]: { value: any } } = {};
    for (const key of Object.keys(updatedRawData)) {
      fields[key] = { value: updatedRawData[key] };
    }

    return httpsCallable(
      getFunctions(undefined, "europe-west1"),
      "updateTenantInfoFields"
    )({
      propertyID: data.property?.id ?? "ERROR",
      unitID: data.id,
      fields: fields,
    }).catch((error) => {
      ToastUtilities.presentServerError(error);
    });
  }
}
